'use client';

import React, { useState, useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';
import { fetchGalleryAlbums, type ApiGalleryAlbum } from '@/lib/gallery';

interface GalleryImage {
  src: string;
  alt: string;
  category: string;
}

// Deterministic bento pattern: repeats every 6 images so any album count still looks intentional.
function spanFor(indexInSet: number): string {
  const position = indexInSet % 6;
  if (position === 0) return 'col-span-2 row-span-2';
  if (position === 3) return 'col-span-2';
  return '';
}

export default function GalleryGrid() {
  const [albums, setAlbums] = useState<ApiGalleryAlbum[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [active, setActive] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchGalleryAlbums()
      .then(setAlbums)
      .catch(() => setError('Could not load the gallery. Please try again shortly.'))
      .finally(() => setLoading(false));
  }, []);

  const allImages: GalleryImage[] = albums.flatMap((album) =>
    album.images.map((img) => ({
      src: img.url,
      alt: `${album.title} - HCAF Prathaa 2026`,
      category: album.title,
    }))
  );

  const categories = ['All', ...albums.filter((a) => a.images.length > 0).map((a) => a.title)];
  const filtered = active === 'All' ? allImages : allImages.filter((img) => img.category === active);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('active'); }); },
      { threshold: 0.05 }
    );
    sectionRef.current?.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [active, albums]);

  return (
    <section ref={sectionRef} className="bg-background w-full px-4 md:px-12 pt-12 md:pt-16 pb-12 md:pb-16">
      <div className="max-w-7xl mx-auto">
        {loading && (
          <div className="text-center text-muted-foreground text-sm py-10">Loading gallery…</div>
        )}

        {!loading && error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-4 text-center text-red-500 text-sm">
            {error}
          </div>
        )}

        {!loading && !error && allImages.length === 0 && (
          <div className="text-center text-muted-foreground text-sm py-10">
            No photos yet - check back soon for moments from Prathaa 2026.
          </div>
        )}

        {!loading && !error && allImages.length > 0 && (
          <>
            {/* Filter Tabs */}
            <div className="flex flex-wrap gap-2 md:gap-3 mb-7 md:mb-10 reveal">
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActive(cat)}
                  className={`px-4 md:px-5 py-2 md:py-2.5 rounded-full text-xs md:text-sm font-bold tracking-wide transition-all duration-200 ${
                    active === cat
                      ? 'bg-primary text-primary-foreground shadow-crimson'
                      : 'bg-card border border-border text-muted-foreground hover:border-primary hover:text-primary'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {active === 'All' ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {filtered.map((img, i) => {
                  const span = spanFor(i);
                  return (
                    <div
                      key={`${img.src}-${i}`}
                      className={`reveal relative overflow-hidden rounded-3xl group ${span}`}
                      style={{ minHeight: span.includes('row-span-2') ? '420px' : '220px', transitionDelay: `${(i % 6) * 60}ms` }}
                    >
                      <AppImage
                        src={img.src}
                        alt={img.alt}
                        fill
                        loading="lazy"
                        className="object-cover grayscale-hover"
                        sizes="(max-width: 768px) 50vw, 25vw"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                      <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="px-3 py-1 rounded-full bg-dark-bg/80 text-white text-xs font-semibold">{img.category}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {filtered.map((img, i) => (
                  <div
                    key={`${img.src}-${i}`}
                    className="reveal relative overflow-hidden rounded-3xl group"
                    style={{ minHeight: '260px', transitionDelay: `${i * 60}ms` }}
                  >
                    <AppImage
                      src={img.src}
                      alt={img.alt}
                      fill
                      loading="lazy"
                      className="object-cover grayscale-hover"
                      sizes="(max-width: 768px) 50vw, 33vw"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-dark-bg/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    <div className="absolute bottom-3 left-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <span className="px-3 py-1 rounded-full bg-dark-bg/80 text-white text-xs font-semibold">{img.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
